"use client";

import * as React from "react";
import * as TooltipPrimitive from "@radix-ui/react-tooltip";

import { cn } from "@/utils";

type TooltipContextValue = {
  onInteraction: {
    onClick: () => void;
    onMouseEnter: () => void;
    onMouseLeave: () => void;
  };
};

const TooltipContext = React.createContext<TooltipContextValue | null>(null);

function TooltipProvider({
  delayDuration = 0,
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Provider>) {
  return (
    <TooltipPrimitive.Provider
      data-slot="tooltip-provider"
      delayDuration={delayDuration}
      {...props}
    />
  );
}

function Tooltip({
  children,
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Root>) {
  const [open, setOpen] = React.useState(false);

  const contextValue: TooltipContextValue = {
    onInteraction: {
      onClick: () => setOpen(!open),
      onMouseEnter: () => setOpen(true),
      onMouseLeave: () => setOpen(false),
    },
  };

  return (
    <TooltipProvider delayDuration={0}>
      <TooltipPrimitive.Root
        data-slot="tooltip"
        open={open}
        onOpenChange={setOpen}
        {...props}
      >
        <TooltipContext.Provider value={contextValue}>
          {children}
        </TooltipContext.Provider>
      </TooltipPrimitive.Root>
    </TooltipProvider>
  );
}

const TooltipTrigger = React.forwardRef<
  React.ElementRef<typeof TooltipPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Trigger>
>(({ onClick, onMouseEnter, onMouseLeave, ...props }, ref) => {
  const context = React.useContext(TooltipContext);

  return (
    <TooltipPrimitive.Trigger
      ref={ref}
      data-slot="tooltip-trigger"
      onClick={(e) => {
        // Handle click for both mouse and touch devices
        context?.onInteraction.onClick();
        onClick?.(e);
      }}
      onMouseEnter={(e) => {
        // Only handle hover on non-touch devices
        if (!("ontouchstart" in window)) {
          context?.onInteraction.onMouseEnter();
        }
        onMouseEnter?.(e);
      }}
      onMouseLeave={(e) => {
        // Only handle hover on non-touch devices
        if (!("ontouchstart" in window)) {
          context?.onInteraction.onMouseLeave();
        }
        onMouseLeave?.(e);
      }}
      {...props}
    />
  );
});
TooltipTrigger.displayName = "TooltipTrigger";

function TooltipContent({
  className,
  sideOffset = 0,
  children,
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Content>) {
  return (
    <TooltipPrimitive.Portal>
      <TooltipPrimitive.Content
        data-slot="tooltip-content"
        sideOffset={sideOffset}
        className={cn(
          "bg-background text-foreground border border-border animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 z-50 w-fit origin-(--radix-tooltip-content-transform-origin) rounded-md !px-5 !py-5 text-sm",
          className
        )}
        {...props}
      >
        {children}
        <TooltipPrimitive.Arrow className="bg-foreground fill-foreground z-50 size-2.5 translate-y-[calc(-50%_-_2px)] rotate-45 rounded-[2px]" />
      </TooltipPrimitive.Content>
    </TooltipPrimitive.Portal>
  );
}

export { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider };
